/*
 * Settings → Integrations → Webhooks. DESIGN-api-and-webhooks.md step 2 (Rob 25/10/2026, 7A
 * and 8A: up to 5 addresses, a second card under API keys; mock-up
 * `Claude outputs/api-mock-v1.html` sections 3–5).
 *
 * ⚠️ **THE SIGNING SECRET IS SHOWN ONCE, FROM THE RESPONSE THAT ADDED THE ADDRESS.** The list
 * only has its first 10 characters. Closing the window throws it away for good; the window
 * says so.
 *
 * ⚠️ **"WHAT WE SENT" IS THE DIAGNOSTIC (rule 9).** When a receiver breaks, this is where the
 * company's own admin sees what it answered and presses Send again — no support ticket, no
 * script. A paused address says why, in a banner, with Turn back on.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Copy, ExternalLink, Loader2, Webhook } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { webhooksApi, apiErrorMessage } from '../lib/api';
import { formatDateGB } from '../lib/dates';
import {
  TICKS, SICKNESS_HINT, canAdd, endpointLine, health, lineIcon, shortUrl, urlProblem, whenParts,
} from '../lib/webhooks';

const DEFAULT_TICKS = ['approved', 'cancelled'];

export default function WebhooksCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);              // 'add' | `test:${id}` | …
  const [adding, setAdding] = useState(false);
  const [url, setUrl] = useState('');
  const [ticks, setTicks] = useState(DEFAULT_TICKS);
  const [sickness, setSickness] = useState(false);
  const [made, setMade] = useState(null);              // { id, host, secret } — shown once
  const [editing, setEditing] = useState(null);        // the address whose ticks are being changed
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [open, setOpen] = useState({});                // which "What we sent" lists are open

  const load = useCallback(() => webhooksApi.list()
    .then((res) => setData(res.data))
    .catch(() => setData(null))
    .finally(() => setLoading(false)), []);

  useEffect(() => { load(); }, [load]);

  const words = data?.tick_words || {};
  const endpoints = data?.endpoints || [];

  const toggle = (setter) => (tick, on) => setter((now) => (on
    ? TICKS.filter((t) => t === tick || now.includes(t))
    : now.filter((t) => t !== tick)));

  const run = async (key, fn, okWords, failWords) => {
    setBusy(key);
    try {
      const res = await fn();
      setData(res.data);
      if (okWords) toast.success(typeof okWords === 'function' ? okWords(res.data) : okWords);
      return res.data;
    } catch (e) {
      toast.error(apiErrorMessage(e, failWords));
      return null;
    } finally {
      setBusy(null);
    }
  };

  const openAdder = () => {
    setUrl(''); setTicks(DEFAULT_TICKS); setSickness(false); setAdding(true);
  };

  const add = async () => {
    const out = await run('add', () => webhooksApi.add(url.trim(), ticks, sickness), null,
      'Couldn’t add the address.');
    if (out?.made) {
      setAdding(false);
      setMade(out.made);
    }
  };

  const sendTest = async (id) => {
    const out = await run(`test:${id}`, () => webhooksApi.test(id), null, 'Couldn’t send the test.');
    if (!out) return;
    setOpen((o) => ({ ...o, [id]: true }));
    if (out.test?.ok) toast.success(`Test sent. ${out.test.words.replace(/^Sent by [^·]+· /, '')}`);
    else toast.error(`The test didn’t get through: ${out.test?.words?.replace(/^Sent by [^·]+· /, '') || 'no answer'}`);
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(made.secret);
      toast.success('Copied.');
    } catch {
      toast.error('Couldn’t copy it — select the secret and copy it by hand.');
    }
  };

  const header = (
    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 shrink-0 rounded-lg bg-primary/20 flex items-center justify-center">
          <Webhook className="w-5 h-5 text-primary" aria-hidden="true" />
        </div>
        <div>
          <h3 className="font-heading font-bold">Webhooks</h3>
          <p className="text-sm text-muted-foreground">
            Tell another system the moment a booking changes, for example a Zapier “catch hook”
          </p>
          <a href="/developers#webhooks" target="_blank" rel="noopener noreferrer"
             className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
             data-testid="webhook-developer-notes">
            What we send, and how to check it’s from us
            <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
          </a>
        </div>
      </div>
      {data && (
        <Button size="sm" className="self-start shrink-0" onClick={openAdder}
                disabled={endpoints.length >= data.max_endpoints} data-testid="webhook-add">
          Add an address
        </Button>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="card-hive p-6" data-testid="integrations-webhooks-card">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="card-hive p-6" data-testid="integrations-webhooks-card">
        {header}
        <p className="text-xs text-muted-foreground">Couldn’t load your webhooks. Please refresh the page.</p>
      </div>
    );
  }

  return (
    <div className="card-hive p-6" data-testid="integrations-webhooks-card">
      {header}

      {endpoints.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="webhooks-none">
          No addresses yet. You can add up to {data.max_endpoints}, each with its own choice of what to send.
        </p>
      ) : (
        <ul className="space-y-4">
          {endpoints.map((ep) => {
            const state = health(ep);
            const isOpen = !!open[ep.id];
            return (
              <li key={ep.id} className="rounded-lg border border-border p-3" data-testid={`webhook-${ep.id}`}>
                {/* ⚠️ Stacked on a phone: side by side, the buttons squeezed the address to one
                    letter a line (seen in the 390px screenshot, 25/10/2026). */}
                <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className={`w-8 h-8 shrink-0 rounded-md flex items-center justify-center text-sm ${
                    state === 'working' ? 'bg-green-500/15' : state === 'failing' ? 'bg-amber-500/15' : 'bg-red-500/15'}`}
                       aria-hidden="true">↗</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold break-all">{shortUrl(ep.url)}</p>
                    <p className="text-xs text-muted-foreground">
                      {endpointLine(ep)}
                      {' · '}
                      <span className={state === 'working' ? 'text-green-700 dark:text-green-400'
                        : state === 'failing' ? 'text-amber-700' : 'text-red-600'} data-testid="webhook-health">
                        {state === 'working' ? 'working' : state === 'failing' ? 'failing — we’re trying again' : 'paused'}
                      </span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Secret <span className="font-mono">{ep.secret_prefix}…</span>
                      {' · '}added by {ep.made_by}{ep.made_at ? ` on ${formatDateGB(ep.made_at)}` : ''}
                    </p>
                  </div>
                  </div>
                  <div className="flex flex-wrap gap-2 sm:justify-end shrink-0">
                    <Button size="sm" variant="outline" disabled={busy === `test:${ep.id}`}
                            onClick={() => sendTest(ep.id)} data-testid="webhook-test">
                      {busy === `test:${ep.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send a test'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditing({ ...ep })}
                            data-testid="webhook-change">Change</Button>
                    {confirmRemove !== ep.id && (
                      <Button size="sm" variant="outline" className="text-red-600 border-red-200"
                              onClick={() => setConfirmRemove(ep.id)} data-testid="webhook-remove">Remove</Button>
                    )}
                  </div>
                </div>

                {confirmRemove === ep.id && (
                  <div className="mt-3 p-3 rounded-md bg-red-50 border border-red-200 text-xs text-red-900"
                       data-testid="webhook-confirm-remove">
                    <p className="mb-2">
                      Remove {ep.host}? Nothing more will be sent there, and its “What we sent” list goes too.
                      To use it again you’d add it again, with a new secret.
                    </p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="destructive" disabled={busy === `remove:${ep.id}`}
                              onClick={async () => {
                                await run(`remove:${ep.id}`, () => webhooksApi.remove(ep.id),
                                  `${ep.host} removed.`, 'Couldn’t remove the address.');
                                setConfirmRemove(null);
                              }}>
                        {busy === `remove:${ep.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Remove it'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setConfirmRemove(null)}>Keep it</Button>
                    </div>
                  </div>
                )}

                {ep.paused_at && (
                  <div className="mt-3 p-3 rounded-md bg-red-50 border border-red-200 text-xs text-red-900 flex flex-wrap items-center gap-2"
                       data-testid="webhook-paused">
                    <span className="flex-1 min-w-[12rem]">
                      ⛔ Paused on {formatDateGB(ep.paused_at)}. {ep.host} failed every time for 3 days, so we
                      stopped. Admins were told. Anything that happened since is held below — Send again once it’s fixed.
                    </span>
                    <Button size="sm" variant="outline" disabled={busy === `resume:${ep.id}`}
                            onClick={() => run(`resume:${ep.id}`, () => webhooksApi.resume(ep.id),
                              `${ep.host} is back on.`, 'Couldn’t turn it back on.')}
                            data-testid="webhook-resume">
                      Turn back on
                    </Button>
                  </div>
                )}

                <button type="button" className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                        onClick={() => setOpen((o) => ({ ...o, [ep.id]: !isOpen }))}
                        aria-expanded={isOpen} data-testid="webhook-sent-toggle">
                  {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  What we sent — the last 30 days ({ep.deliveries.length})
                </button>

                {isOpen && (
                  ep.deliveries.length === 0 ? (
                    <p className="text-xs text-muted-foreground mt-2">Nothing yet. Press Send a test to try it.</p>
                  ) : (
                    <ul className="mt-2 divide-y divide-border" data-testid="webhook-deliveries">
                      {ep.deliveries.map((ln) => {
                        const icon = lineIcon(ln);
                        const [day, time] = whenParts(ln.at);
                        return (
                          <li key={ln.id} className="py-2 flex items-start gap-3 text-xs" data-testid="webhook-delivery">
                            <span className={`w-6 h-6 shrink-0 rounded-md flex items-center justify-center ${icon.cls}`}
                                  title={icon.label} aria-label={icon.label}>{icon.char}</span>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium">{ln.title}</p>
                              <p className="text-muted-foreground break-words">{ln.words}</p>
                              {ln.reply && ln.state !== 'sent' && (
                                <details className="mt-1">
                                  <summary className="cursor-pointer text-muted-foreground">What it answered</summary>
                                  <pre className="mt-1 p-2 bg-secondary rounded whitespace-pre-wrap break-all max-h-40 overflow-auto">{ln.reply}</pre>
                                </details>
                              )}
                              {ln.can_resend && (
                                <Button size="sm" variant="outline" className="mt-1 h-7"
                                        disabled={busy === `resend:${ln.id}` || !!ep.paused_at}
                                        title={ep.paused_at ? 'Turn the address back on first' : undefined}
                                        onClick={() => run(`resend:${ln.id}`, () => webhooksApi.resend(ln.id),
                                          'Sending it again now.', 'Couldn’t send it again.')}
                                        data-testid="webhook-resend">
                                  Send again
                                </Button>
                              )}
                            </div>
                            <div className="text-right text-muted-foreground shrink-0">
                              <div>{day}</div><div>{time}</div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )
                )}
              </li>
            );
          })}
        </ul>
      )}

      <p className="text-xs text-muted-foreground mt-4">
        Messages never carry leave reasons, decline notes or the names of private leave types, and sickness shows as
        “Time off” unless you tick it for an address. We only send to https addresses on the public internet, and try
        again for about two days if one doesn’t answer. Adding, removing and pausing an address are listed in
        Settings → Security → Sign-ins.
      </p>

      {/* Adding an address */}
      <Dialog open={adding} onOpenChange={(o) => { if (busy !== 'add') setAdding(o); }}>
        <DialogContent className="max-w-md" data-testid="webhook-adder">
          <DialogHeader>
            <DialogTitle>Add a webhook address</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="webhook-url">Address (it must start https://)</Label>
              <Input id="webhook-url" value={url} maxLength={500}
                     placeholder="https://hooks.zapier.com/hooks/catch/…"
                     onChange={(e) => setUrl(e.target.value)} data-testid="webhook-url" />
              {url && urlProblem(url) && (
                <p className="text-xs text-red-600 mt-1">{urlProblem(url)}</p>
              )}
            </div>
            <TickList words={words} ticks={ticks} onTick={toggle(setTicks)}
                      sickness={sickness} onSickness={setSickness} prefix="add" />
            <p className="text-xs text-muted-foreground">
              Refused: anything that isn’t https, and any address inside a private network (like 192.168.x.x), so
              nobody can use us to poke at your office network.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAdding(false)} disabled={busy === 'add'}>Cancel</Button>
              <Button onClick={add} disabled={!canAdd(url, ticks) || busy === 'add'} data-testid="webhook-save">
                {busy === 'add' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* The secret — once */}
      <Dialog open={!!made} onOpenChange={(o) => { if (!o) setMade(null); }}>
        <DialogContent className="max-w-lg" data-testid="webhook-made">
          <DialogHeader>
            <DialogTitle>Signing secret for {made?.host}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">
              Every message we send is signed with this, so the system receiving it can check it really came from us.
            </p>
            <div className="font-mono text-sm bg-secondary rounded-md p-3 break-all select-all" data-testid="webhook-secret">
              {made?.secret}
            </div>
            <Button onClick={copySecret} size="sm"><Copy className="w-4 h-4 mr-1" aria-hidden="true" />Copy</Button>
            <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900">
              <strong>This is the only time you’ll see it.</strong> If it’s lost, remove the address and add it again.
              Zapier doesn’t need it; a system your developers built should check it —{' '}
              <a href="/developers#webhooks" target="_blank" rel="noopener noreferrer" className="underline">here’s how</a>.
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" disabled={busy === `test:${made?.id}`}
                      onClick={() => made && sendTest(made.id)} data-testid="webhook-made-test">
                {busy === `test:${made?.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send a test'}
              </Button>
              <Button onClick={() => setMade(null)} data-testid="webhook-made-done">I’ve copied it</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Changing what an address gets */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o && !busy) setEditing(null); }}>
        <DialogContent className="max-w-md" data-testid="webhook-editor">
          <DialogHeader>
            <DialogTitle>What {editing?.host} gets</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <TickList words={words} ticks={editing.events}
                        onTick={(t, on) => setEditing((e) => ({
                          ...e,
                          events: on ? TICKS.filter((x) => x === t || e.events.includes(x)) : e.events.filter((x) => x !== t),
                        }))}
                        sickness={editing.show_sickness}
                        onSickness={(v) => setEditing((e) => ({ ...e, show_sickness: v }))} prefix="edit" />
              <p className="text-xs text-muted-foreground">
                To send to a different address, remove this one and add the new one.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditing(null)} disabled={busy === 'edit'}>Cancel</Button>
                <Button disabled={!editing.events.length || busy === 'edit'} data-testid="webhook-edit-save"
                        onClick={async () => {
                          const out = await run('edit', () => webhooksApi.change(editing.id,
                            { events: editing.events, show_sickness: editing.show_sickness }),
                          'Saved.', 'Couldn’t save that.');
                          if (out) setEditing(null);
                        }}>
                  {busy === 'edit' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TickList({ words, ticks, onTick, sickness, onSickness, prefix }) {
  return (
    <div className="space-y-3">
      <p className="text-sm font-medium">Send when a booking is…</p>
      {TICKS.map((t) => (
        <div key={t} className="flex items-center gap-2">
          <Checkbox id={`wh-${prefix}-${t}`} checked={ticks.includes(t)}
                    onCheckedChange={(on) => onTick(t, !!on)} data-testid={`webhook-tick-${t}`} />
          <Label htmlFor={`wh-${prefix}-${t}`} className="text-sm cursor-pointer">{words[t] || t}</Label>
        </div>
      ))}
      <div className="flex items-start gap-2 pt-1">
        <Checkbox id={`wh-${prefix}-sickness`} checked={!!sickness}
                  onCheckedChange={(on) => onSickness(!!on)} data-testid="webhook-tick-sickness" />
        <div>
          <Label htmlFor={`wh-${prefix}-sickness`} className="text-sm cursor-pointer">Show sickness as sickness</Label>
          <p className="text-xs text-muted-foreground">{SICKNESS_HINT}</p>
        </div>
      </div>
    </div>
  );
}
